import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import DataTable from "../../components/globals/data-table/data-table";
import { useForm, Controller, FormProvider } from "react-hook-form";
import debounce from "lodash.debounce";
import {
  deleteClassProfile,
  getAllProfileClass,
  storeClassProfile,
  updateClassProfile,
} from "../../services/profile-class.service";
import {
  getInstitutions,
  getInstitutionsByName,
} from "../../services/institution";
import Modal3 from "../../components/modal3/modal3";
import { useTranslation } from "react-i18next";
import AsyncSelect from "react-select/async";
import TextInput2 from "../../components/form-elements/TexInput2/TextInput2";
import TextArea2 from "../../components/form-elements/TextArea2/textArea2";
import { getAllGradeLevels } from "../../services/grade-levels.service";
import { getAllAcademicPeriod } from "../../services/academic-period.service";
import SelectInput from "../../components/form-elements/SelectInput/SelectInput";
import { getAllSubjectArea } from "../../services/subject-area.service";
import useSnackbarStore from "../../store/snackbarStore";

const ClassProfile = () => {
  const [profileClassList, setProfileClassList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [openModal, setOpenModal] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [,setInstitutions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [gradeLevelOptions, setGradeLevelOptions] = useState([]);
  const [subjectAreaOptions, setSubjectAreaOptions] = useState([]);
  const methods = useForm();
  const { t } = useTranslation();
  const { show } = useSnackbarStore();
  const {
    handleSubmit,
    setValue,
    reset,
    control,
    formState: { errors },
  } = methods;

  const { data: academicPeriods } = useQuery(
    "academic-periods",
    getAllAcademicPeriod,
    {
      retries: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      retry: (failureCount, error) => {
        if (error.status === 404) return false;
        return failureCount <= 3;
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  const { data: gradeLevels } = useQuery("grade-levels", getAllGradeLevels, {
    retries: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    retry: (failureCount, error) => {
      if (error.status === 404) return false;
      return failureCount <= 3;
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const { data: subjectAreas } = useQuery("subject-areas", getAllSubjectArea, {
    retries: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    retry: (failureCount, error) => {
      if (error.status === 404) return false;
      return failureCount <= 3;
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const columns = [
    { ref: "id", value: "ID" },
    { ref: "class_name", value: "Nombre" },
    { ref: "class_name", value: "N° de Estudiantes" },
    { ref: "class_name", value: "Edad de Estudiantes" },
    { ref: "class_name", value: "Consideraciones especiales" },
  ];

  const SelectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: "rgb(182 182 182 / 0.5)",
      borderColor: "transparent",
      borderRadius: "8px",
      caretColor: "white",
      "&:hover": { borderColor: "transparent" }, // Borde en hover
    }),
    input: (provided) => ({
      ...provided,
      color: "rgb(38, 38, 38)",
      fontWeight: "bold", // Establece el color del texto del input a blanco
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? "#F2F2F2" : "#FEFEFE",
      color: "black",
      cursor: "pointer",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "#FEFEFE", // Color del indicador
      "&:hover": {
        color: "#3FB2E3", // Color del indicador en hover
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "rgb(38, 38, 38)", // Establece el color del texto de la opción seleccionada a blanco
      fontWeight: "bold",
    }),
    clearIndicator: (styles) => ({
      ...styles,
      color: "black", // Aquí puedes poner el color que desees
      ":hover": {
        color: "black", // Color cuando se pasa el ratón por encima (hover)
      },
    }),
  };

  const handleNew = () => {
    setOpenModal(true);
    reset();
  };

  const handleEdit = (row) => {
    const item = row.data;
    Object.keys(item).forEach((key) => {
      if (key === "subjectAreas" && Array.isArray(item[key])) {
        // Transforma y establece subjectAreas para AsyncSelect en formato { label, value }
        setValue(
          "subjectAreas",
          item[key].map((sa) => ({ label: sa.name, value: sa.id }))
        );
      } else if (key === "gradeLevels" && Array.isArray(item[key])) {
        // Transforma y establece gradeLevels para AsyncSelect en formato { label, value }
        setValue(
          "gradeLevels",
          item[key].map((sa) => ({ label: sa.name, value: sa.id }))
        );
      } else if (key === "institution") {
        getInstitutionsByName(item[key].name).then((res) => {
          setInstitutions(res);
          const findInstitution = res.find((e) => e.id === item[key].id);
          if (findInstitution) {
            setValue("institution", {
              label: findInstitution.name,
              value: findInstitution.id,
            });
          }
        });
      } else if (key === "academicPeriod") {
        // Establece academicPeriod si existe en el formato correcto
        setValue( "academicPeriod",item[key].id);
      } else {
        // Para todos los demás valores del formulario
        setValue(key, item[key]);
      }
    });

    setSelected(item);
    setOpenModal(true);
  };

  const handlePage = (nextPage) => {
    setPage(nextPage);
    setSearchLoading(true);
    mutationClassProfileList.mutate({ page: nextPage });
  };

  const onClose = () => {
    setOpenModal(false);
    reset();
    setSelected(null);
  };

  const handleRemove = () => {
    setLoading(true);
    mutationRemoveClassProfile.mutate(selected.id)
  }

  const filterGradeLevelOptions = (inputValue) => {
    return gradeLevelOptions.filter((i) =>
      i.label.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  const filterSubjectAreaOptions = (inputValue) => {
    return subjectAreaOptions.filter((i) =>
      i.label.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  const loadGradeLevelOptions = (inputValue, callback) => {
    setTimeout(() => {
      callback(filterGradeLevelOptions(inputValue));
    }, 0);
  };

  const loadSubjectAreaOptions = (inputValue, callback) => {
    setTimeout(() => {
      callback(filterSubjectAreaOptions(inputValue));
    }, 0);
  };

  // eslint-disable-next-line
  const debouncedLoadOptions = useCallback(
    debounce((inputValue, callback) => {
      if (!inputValue) {
        callback([]);
        return;
      }

      // useCallback para memorizar la función debounced
      getInstitutionsByName(inputValue).then((institutions) => {
        const options = institutions.map((institution) => ({
          label: institution.name,
          value: institution.id,
        }));
        if (options.length === 0) {
          // Si no hay opciones, muestra el modal
          setValue("newInstitution", inputValue);
        } else {
          setValue("newInstitution", "");
          // Si hay opciones, asegúrate de que el modal no se muestre
        }
        callback(options);
      });
    }, 1000),
    []
  );

  // eslint-disable-next-line
  const loadInstitutionOptions = (inputValue, callback) => {
    debouncedLoadOptions(inputValue, callback);
  };

  const onSubmit = (formData) => {
    const formDataToSend = new FormData();

    if (formData?.class_name) {
      formDataToSend.append("class_name", formData.class_name);
    }

    if (formData?.number_students) {
      formDataToSend.append("number_students", formData.number_students);
    }

    if (formData?.student_ages) {
      formDataToSend.append("student_ages", formData.student_ages);
    }

    if (formData?.special_considerations) {
      formDataToSend.append("special_considerations", formData.special_considerations);
    }

    if (formData?.gradeLevels?.length) {
      formDataToSend.append(
        "grade_level_ids",
        JSON.stringify(formData.gradeLevels.map((e) => e.value))
      );
    }

    if (formData?.subjectAreas?.length) {
      formDataToSend.append(
        "subject_area_ids",
        JSON.stringify(formData.subjectAreas.map((e) => e.value))
      );
    }

    if (formData?.academicPeriod) {
      formDataToSend.append(
        "academic_period_id",
        Number(formData.academicPeriod)
      );
    }

    if (Object.values(formData?.institution)?.length) {
      formDataToSend.append("institution_id", formData.institution.value);
    }

    for (let [key, value] of formDataToSend.entries()) {
      console.log(`key ${key} --- value ${value}`);
    }

    setLoading(true);
    const body = formDataToSend;
    if (selected) {
      mutationUpdateClassProfile.mutate({
        id: selected.id,
        body,
      });
    } else {
      mutationStoreClassProfile.mutate(body);
    }
  };

  const mutationClassProfileList = useMutation(getAllProfileClass, {
    onSuccess: (res) => {
      setProfileClassList(res?.results);

      setSearchLoading(false);
    },
    onError: () => {},
  });

  const mutationStoreClassProfile = useMutation(storeClassProfile, {
    onSuccess: (res) => {
      mutationClassProfileList.mutate(page);
      const item = res;
      Object.keys(item).forEach((key) => {
        if (key === "subjectAreas" && Array.isArray(item[key])) {
          // Transforma y establece subjectAreas para AsyncSelect en formato { label, value }
          setValue(
            "subjectAreas",
            item[key].map((sa) => ({ label: sa.name, value: sa.id }))
          );
        } else if (key === "gradeLevels" && Array.isArray(item[key])) {
          // Transforma y establece gradeLevels para AsyncSelect en formato { label, value }
          setValue(
            "gradeLevels",
            item[key].map((sa) => ({ label: sa.name, value: sa.id }))
          );
        } else if (key === "institution") {
          getInstitutionsByName(item[key].name).then((res) => {
            setInstitutions(res);
            const findInstitution = res.find((e) => e.id === item[key].id);
            if (findInstitution) {
              setValue("institution", {
                label: findInstitution.name,
                value: findInstitution.id,
              });
            }
          });
        } else if (key === "academicPeriod") {
          // Establece academicPeriod si existe en el formato correcto
          setValue( "academicPeriod",item[key].id);
        } else {
          // Para todos los demás valores del formulario
          setValue(key, item[key]);
        }
      });
      setSelected(item);

      setLoading(false);
      show("Registro Actualizado");
    },
    onError: () => {},
  });

  const mutationInstitutions = useMutation(getInstitutions, {
    onSuccess: (res) => {
      setInstitutions(res.results);
    },
    onError: () => {},
  });

  const mutationUpdateClassProfile = useMutation(updateClassProfile, {
    onSuccess: (data) => {
      const item = data;
      Object.keys(item).forEach((key) => {
        if (key === "subjectAreas" && Array.isArray(item[key])) {
          // Transforma y establece subjectAreas para AsyncSelect en formato { label, value }
          setValue(
            "subjectAreas",
            item[key].map((sa) => ({ label: sa.name, value: sa.id }))
          );
        } else if (key === "gradeLevels" && Array.isArray(item[key])) {
          // Transforma y establece gradeLevels para AsyncSelect en formato { label, value }
          setValue(
            "gradeLevels",
            item[key].map((sa) => ({ label: sa.name, value: sa.id }))
          );
        } else if (key === "institution") {
          getInstitutionsByName(item[key].name).then((res) => {
            setInstitutions(res);
            const findInstitution = res.find((e) => e.id === item[key].id);
            if (findInstitution) {
              setValue("institution", {
                label: findInstitution.name,
                value: findInstitution.id,
              });
            }
          });
        } else if (key === "academicPeriod") {
          // Establece academicPeriod si existe en el formato correcto
          setValue( "academicPeriod",item[key].id);
        } else {
          // Para todos los demás valores del formulario
          setValue(key, item[key]);
        }
      });
      setSelected(item);

      setLoading(false);
      show("Registro Actualizado");
    },
    onError: (error) => {},
  });

  const mutationRemoveClassProfile = useMutation(deleteClassProfile, {
    onSuccess: (res) => {
      setLoading(false);
      mutationClassProfileList.mutate(page);
      onClose();
    },
    onError: () => {},
  });

  // eslint-disable-next-line
  const debouncedSearch = useCallback(
    debounce((searchTerm) => {
      setSearchLoading(true);
      mutationClassProfileList.mutate({ page, name: searchTerm });
    }, 500),

    [page]
  );

  useEffect(() => {
    if (gradeLevels) {
      const transformed = gradeLevels.map((level) => ({
        value: level.id,
        label: level.name,
      }));
      setGradeLevelOptions(transformed);
    }
  }, [gradeLevels]);

  useEffect(() => {
    if (subjectAreas) {
      const transformed = subjectAreas.map((level) => ({
        value: level.id,
        label: level.name,
      }));
      setSubjectAreaOptions(transformed);
    }
  }, [subjectAreas]);

  useEffect(() => {
    mutationInstitutions.mutate({ page });
    mutationClassProfileList.mutate(page);
    // eslint-disable-next-line
  }, []);

  return (
    <div>
      <div className="my-4 flex justify-end items-center">
        <div className="relative w-64 mr-4">
          <input
            type="text"
            className="border border-gray-300 rounded-lg py-2 pl-10 pr-4"
            placeholder="Buscar perfil de clase"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              debouncedSearch(e.target.value);
            }}
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 text-gray-500 absolute left-3 top-1/2 transform -translate-y-1/2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <button
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded button-primary"
          onClick={() => handleNew()}
        >
          Agregar
        </button>
      </div>
      <div>
        <DataTable
          data={profileClassList}
          height={500}
          headerColumns={columns}
          handleSelect={handleEdit}
          loading={searchLoading}
        />
        <div className="flex justify-end">
          <div className=" flex p-2 gap-2">
            {page > 1 && (
              <div>
                <button
                  onClick={() => handlePage(page - 1)}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded button-primary"
                >
                  Anterior
                </button>
              </div>
            )}

            <div>
              <button
                onClick={() => handlePage(page + 1)}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded button-primary"
              >
                Siguiente
              </button>
            </div>
          </div>
        </div>
      </div>
      <Modal3 isOpen={openModal} onClose={onClose}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div>
              <div className="input-label text-shaiaBlue w-full flex items-center justify-center mb-0 ">
                Perfil de Clase
              </div>

              <div className="input-text flex flex-col justify-center w-full mt-0">
                <div className="input-row">
                  <TextInput2
                    label="Nombre de Clase"
                    name="class_name"
                    placeHolder=""
                    required
                  />
                  <TextInput2
                    label="N° de Estudiantes"
                    name="number_students"
                    placeHolder=""
                    required
                  />

                  <TextInput2
                    label="Edad de Estudiantes"
                    name="student_ages"
                    placeHolder=""
                    required
                  />
                </div>
                <div className="input-row">
                  <TextArea2
                    label="Consideraciones especiales"
                    name="special_considerations"
                    placeholder="Escribe las consideraciones especiales"
                    required
                  />
                </div>
                <div className="input-row">
                  <div className="input-text mt-0">
                    <label className="input-label text-shaiaBlue">
                      Institución
                    </label>
                    <Controller
                      name="institution"
                      control={control}
                      rules={{ required: t("campo_requerido") }}
                      render={({ field }) => (
                        <AsyncSelect
                          {...field}
                          cacheOptions
                          defaultOptions
                          loadOptions={loadInstitutionOptions}
                          onInputChange={(value) => {
                            setSearchTerm(value);
                          }}
                          onChange={(selectedOption) => {
                            field.onChange(selectedOption);
                            setValue("institution", selectedOption, {
                              shouldValidate: true,
                            });
                          }}
                          value={field.value}
                          styles={SelectStyles}
                          placeholder={t(
                            "registro_campo_institution_placeholder"
                          )}
                          getOptionLabel={(option) => option.label}
                          getOptionValue={(option) => option.value}
                          disabled={!loading}
                        />
                      )}
                    />
                    {errors.institution && (
                      <span className="text-white pt-1 font-bold">
                        {errors.institution.message}
                      </span>
                    )}
                  </div>
                  <div className="mt-[-33px]">
                    <SelectInput
                      control={methods.control}
                      name="academicPeriod"
                      label="Periodo Academico"
                      options={academicPeriods?.map((e) => ({
                        value: e.id,
                        description: e.name,
                      }))}
                    />
                  </div>
                </div>
                <div className="input-row">
                  <div className="flex flex-col gap-2">
                    <div>
                      <label className="input-label text-shaiaBlue">
                        Niveles de Grado
                      </label>
                    </div>
                    <div>
                      <Controller
                        name="gradeLevels"
                        control={control}
                        render={({ field }) => (
                          <AsyncSelect
                            {...field}
                            cacheOptions
                            defaultOptions={gradeLevelOptions}
                            loadOptions={loadGradeLevelOptions}
                            isMulti
                            placeholder="Buscar niveles de grado"
                          />
                        )}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <div>
                      <label className="input-label text-shaiaBlue">
                        Materias
                      </label>
                    </div>
                    <div>
                      <Controller
                        name="subjectAreas"
                        control={control}
                        render={({ field }) => (
                          <AsyncSelect
                            {...field}
                            cacheOptions
                            defaultOptions={subjectAreaOptions}
                            loadOptions={loadSubjectAreaOptions}
                            isMulti
                            placeholder="Buscar materias"
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="input-row mt-4 pt-8">
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded button-primary"
              >
                {loading ? "Enviando..." : selected ? "Actualizar" : "Crear"}
              </button>
             {
              selected && (
                <button
                type="button"
                onClick={handleRemove}
                disabled={loading}
                className="bg-shaiaBlue text-white font-bold py-2 px-4 rounded button-primary"
              >
                {loading ? "Enviando..." : "Eliminar"}
              </button>
              )
             }
            </div>
          </form>
        </FormProvider>
      </Modal3>
    </div>
  );
};

export default ClassProfile;
